import { supabase } from "@/lib/supabase";
import type { PostgrestError } from "@supabase/supabase-js";
import type { RestaurantId, Table } from "@/types/restaurant";
import { fetchAllPages } from "./paging";

// ---------------------------------------------------------------------------
// Tables  (floor plan)
// ---------------------------------------------------------------------------

export async function listTables(restaurantId?: RestaurantId): Promise<Table[]> {
  const data = await fetchAllPages((from, to) => {
    let query = supabase.from("tables").select("*");
    if (restaurantId) query = query.eq("restaurant_id", restaurantId);
    // table_id is the text PK — unique, so a stable pagination order.
    return query.order("table_id", { ascending: true }).range(from, to);
  });
  return data as Table[];
}

// ---------------------------------------------------------------------------
// Tables — operator can create + delete; status is POS-driven (no update).
// ---------------------------------------------------------------------------

/** Subset of `Table` the operator can author. */
export interface TableInput {
  table_id: string;
  restaurant_id: RestaurantId;
  name: string;
  capacity: number;
}

/**
 * Find the next free `T<NN>` id across the whole `tables` table. The
 * compound key is global (text PK), so we pick the smallest integer that
 * doesn't collide. Format keeps the demo's `T01`/`T02`/... visual style.
 */
export async function suggestNextTableId(): Promise<string> {
  return (await suggestNextTableIds(1))[0]!;
}

/**
 * First `count` free `T<NN>` ids in one scan — the batch flavour used by
 * quick-generate and duplicate, so a whole bulk insert costs one id query.
 */
export async function suggestNextTableIds(count: number): Promise<string[]> {
  // table_id is a GLOBAL text PK, so the suggestion must dodge every venue's
  // ids — but only the T<NN> pattern matters, so filter server-side instead of
  // scanning provider-imported GUID rows too.
  const data = (await fetchAllPages((from, to) =>
    supabase
      .from("tables")
      .select("table_id")
      .like("table_id", "T%")
      .order("table_id", { ascending: true })
      .range(from, to),
  )) as Array<{ table_id: string }>;
  const used = new Set<number>();
  for (const row of data) {
    const m = /^T(\d+)$/.exec(row.table_id);
    if (m && m[1]) used.add(parseInt(m[1], 10));
  }
  const ids: string[] = [];
  let next = 1;
  while (ids.length < count) {
    if (!used.has(next)) ids.push(`T${String(next).padStart(2, "0")}`);
    next += 1;
  }
  return ids;
}

export async function createTable(input: TableInput): Promise<Table> {
  // Status defaults to 'free' via the schema; we don't pass it.
  const { data, error } = await supabase
    .from("tables")
    .insert(input)
    .select()
    .single();
  if (error) throw error;
  return data as Table;
}

/**
 * Update a table's `name` or `capacity`. Status is excluded on purpose —
 * the operator should not flip a table between free/occupied; that's
 * exclusively a POS pipeline concern.
 */
export async function updateTable(
  tableId: string,
  patch: { name?: string; capacity?: number },
): Promise<Table> {
  const { data, error } = await supabase
    .from("tables")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("table_id", tableId)
    .select()
    .single();
  if (error) throw error;
  return data as Table;
}

/** One atomic bulk insert — a duplicate PK fails the whole batch (no partial state). */
export async function createTables(inputs: TableInput[]): Promise<Table[]> {
  const { data, error } = await supabase.from("tables").insert(inputs).select();
  if (error) throw error;
  return (data ?? []) as Table[];
}

/**
 * Next number for an auto-generated table NAME within one venue: one past the
 * highest integer appearing in the venue's existing table names ("Masa 9 Geam"
 * → 10, none → 1). Names number per venue even though ids share a global
 * sequence — a fresh venue's first batch reads "Table 1", not "Table 21".
 */
export function nextTableNameNumber(existingNames: string[]): number {
  let maxSeen = 0;
  for (const name of existingNames) {
    for (const m of name.matchAll(/\d+/g)) {
      const n = parseInt(m[0], 10);
      if (Number.isFinite(n) && n > maxSeen) maxSeen = n;
    }
  }
  return maxSeen + 1;
}

/**
 * Bulk-create tables with auto-allocated `T<NN>` ids. Because the PK is global,
 * another session can claim a suggested id between the scan and the insert —
 * the insert is atomic, so on a 23505 we simply re-scan (which now sees the
 * taken ids) and retry. Empty names are numbered per venue from
 * `existingNames` ("Table" → "Table 9" after "Masa 8 Geam"), so venue B's
 * first batch starts at 1 regardless of where the global id sequence sits.
 */
export async function createTablesAutoId(
  restaurantId: RestaurantId,
  specs: Array<{ name: string; capacity: number }>,
  namePrefix = "Table",
  existingNames: string[] = [],
): Promise<Table[]> {
  for (let attempt = 0; ; attempt += 1) {
    const ids = await suggestNextTableIds(specs.length);
    let nameNum = nextTableNameNumber(existingNames);
    const inputs: TableInput[] = specs.map((spec, i) => {
      return {
        table_id: ids[i]!,
        restaurant_id: restaurantId,
        name: spec.name.trim() || `${namePrefix} ${nameNum++}`,
        capacity: spec.capacity,
      };
    });
    try {
      return await createTables(inputs);
    } catch (error) {
      const code = (error as PostgrestError | null)?.code;
      if (code !== "23505" || attempt >= 2) throw error;
    }
  }
}

/**
 * Bulk delete. The `restaurant_id` filter is a safety guard the single-row
 * variant lacks — table_id is a global PK and operator RLS spans venues.
 */
export async function deleteTables(
  tableIds: string[],
  restaurantId: RestaurantId,
): Promise<void> {
  if (tableIds.length === 0) return;
  const { error } = await supabase
    .from("tables")
    .delete()
    .in("table_id", tableIds)
    .eq("restaurant_id", restaurantId);
  if (error) throw error;
}
